import React from 'react'
import {
  BaseComponent,
} from 'belle-js'
import ContactForm from 'components/Onboarding/Accounts/ContactForm'
import withOnboardingSection from 'components/Onboarding/withOnboardingSection'
import { allFinancialAccountTypes, SCHWAB_CHARITABLE } from 'utils/financialAccountTypes'
import {
  createContact,
  createContactFinancialAccountRole,
  createContactRelationship,
  deleteContactRelationship,
  updateContact,
} from 'utils/apiRequest'
import { allContactTypes, successorDonor } from 'utils/contactTypes'
import { mapContactValues } from 'utils/formDataMapping'
import TrusteeForm from 'components/Onboarding/TrusteeForm'

const OnboardingSection = withOnboardingSection(ContactForm)

class AddEditContact extends BaseComponent {

  constructor (props) {
    super(props)

    this.bind(
      'addContactData',
      'createContactHandler',
      'editContactData',
      'updateContactRelationshipHandler',
      'updateContactHandler',
      'isSuccessorDonor',
    )
  }

  async createContactHandler (data) {
    const { payload } = this.props.location
    const { relationship } = data
    const { contactType, financialAccountId, beneficiaryType } = payload
    const contactTypeConfig = allContactTypes[contactType]
    const contact = await createContact(data)
    const contactId = contact.data.id
    const { role } = contactTypeConfig
    const params = {
      financialAccount: financialAccountId,
      relatedContact: contactId,
      role,
      beneficiaryType
    }
    const contactRole = await createContactFinancialAccountRole(params)
    let contactRelationship = null
    if (relationship) {
      const contactRelationshipBody = { relatedContact: contactId, role: relationship }
      contactRelationship = await createContactRelationship(contactRelationshipBody)
    }
    return { contact, contactFinancialAccountRole: contactRole, contactRelationship }
  }

  async updateContactRelationshipHandler (data) {
    const { relationship } = data
    const {
      currentContactRelationship,
      currentContactReciprocalRole
    } = this.props

    const { payload } = this.props.location
    const { contactId } = payload
    let contactRelationship = null
    const contactRelationshipBody = { relatedContact: contactId, role: relationship }

    if (currentContactReciprocalRole) {
      if (relationship && (relationship !== currentContactReciprocalRole.name)) {
        await deleteContactRelationship(currentContactRelationship.id)
        contactRelationship = await createContactRelationship(contactRelationshipBody)
        return {
          contactRelationship,
          previousContactRelationshipId: currentContactRelationship.id
        }
      }
    } else {
      if (relationship) {
        contactRelationship = await createContactRelationship(contactRelationshipBody)
        return { contactRelationship }
      }
    }
  }

  async updateContactHandler (data) {
    const { payload } = this.props.location
    const { contactId } = payload

    const contact = await updateContact(contactId, data)
    const finalRelationshipData = await this.updateContactRelationshipHandler(data)
    return { contact, ...finalRelationshipData }
  }

  isSuccessorDonor () {
    const { location, currentAccount: { financialAccount } } = this.props
    if (!financialAccount) return
    const { payload: { contactType } } = location
    const { DONOR_ADVISED_FUND } = SCHWAB_CHARITABLE.ACCOUNT_TYPES
    return (
      DONOR_ADVISED_FUND === financialAccount.subType &&
      contactType === 'beneficiaries'
    )
  }

  addContactData (data) {
    const {
      addContact,
      addContactFinancialAccountRole,
      addContactRelationship,
    } = this.props
    const { contact, contactFinancialAccountRole, contactRelationship } = data
    addContact(contact)
    addContactFinancialAccountRole(contactFinancialAccountRole)
    if (contactRelationship) {
      addContactRelationship(contactRelationship)
    }
  }

  editContactData (data) {
    const {
      editContact,
      addContactRelationship,
      removeContactRelationship
    } = this.props
    const { contact, contactRelationship, previousContactRelationshipId } = data
    editContact(contact)
    if (previousContactRelationshipId) {
      removeContactRelationship(previousContactRelationshipId)
    }

    if (contactRelationship) {
      addContactRelationship(contactRelationship)
    }
  }

  render () {
    const {
      addContactRelationship,
      contactReciprocalRoles,
      contactTypes,
      currentFinancialAccountContacts,
      currentContactReciprocalRole,
      editFinancialAccount,
      goOnboardingContacts,
      hide,
      isMobile,
      isLoading,
      location,
    } = this.props

    const { payload } = location
    const {
      financialAccountId,
      contactId,
      financialAccountType,
      contactType
    } = payload
    const contactTypeConfig = allContactTypes[contactType]

    if (!contactTypeConfig) {
      return null
    }
    const financialAccountTypeConfig = allFinancialAccountTypes[financialAccountType]
    const isSuccessorDonor = this.isSuccessorDonor()
    const contact = currentFinancialAccountContacts.find(c => (c.id === contactId))
    const section = `${financialAccountTypeConfig.SECTION}${contactTypeConfig.section}`
    const isAdd = !contact

    let description = isAdd ? contactTypeConfig.addDescription : contactTypeConfig.editDescription
    const title = isAdd ? `Add ${contactTypeConfig.DISPLAY}` : `Edit ${contactTypeConfig.DISPLAY}`
    const formSubmitHandler = isAdd ? this.createContactHandler : this.updateContactHandler
    const setData = isAdd ? this.addContactData : this.editContactData

    const args = [financialAccountId, financialAccountType, contactTypeConfig.routeType]
    const returnToContactIndexPage = () => (goOnboardingContacts(...args))
    const { role } = contactTypeConfig
    let formName = role === 'Successor Custodian' ? contactTypeConfig.DISPLAY : title

    const overwroteRequiredFields = contactTypeConfig.overrideRequired || []

    if (isSuccessorDonor) {
      description = isAdd ? successorDonor.addDescription : successorDonor.editDescription
      formName = 'Successor Donor'
    }

    if (role === 'Trustee') {
      return (
        <TrusteeForm
          addContactData={this.addContactData}
          createContactHandler={this.createContactHandler}
          editContactData={this.editContactData}
          contact={contact}
          editFinancialAccount={editFinancialAccount}
          hide={hide}
          isAdd={isAdd}
          isLoading={isLoading}
          isMobile={isMobile}
          returnToContactIndexPage={returnToContactIndexPage}
          updateContactHandler={this.updateContactHandler}
          upperTitle={financialAccountTypeConfig.DISPLAY} />
      )
    }

    return (
      <OnboardingSection
        addContactRelationship={addContactRelationship}
        contactReciprocalRoles={contactReciprocalRoles}
        contactRecordTypes={contactTypes}
        currentContactReciprocalRole={currentContactReciprocalRole}
        formName={formName}
        description={[description]}
        isSuccessorDonor={isSuccessorDonor}
        upperTitle={financialAccountTypeConfig.DISPLAY}
        section={section}
        goNextSection={returnToContactIndexPage}
        hide={hide}
        isAdd={isAdd}
        isMobile={isMobile}
        data={contact}
        setData={setData}
        mapValues={mapContactValues}
        submitAction={formSubmitHandler}
        overwroteRequiredFields={overwroteRequiredFields}
        contactType={contactTypeConfig.DISPLAY}
        allowedContactRecordTypes={contactTypeConfig.allowedContactRecordTypes}
        requiredFields={contactTypeConfig.fields}
        isLoading={isLoading} />
    )
  }

}

export default AddEditContact



// WEBPACK FOOTER //
// ./src/components/Onboarding/Accounts/AddEditContact.js